import { memo, useState, useCallback, useRef, useEffect } from "react"
import {
  BaseEdge,
  EdgeLabelRenderer,
  getBezierPath,
  getSmoothStepPath,
  getStraightPath,
  useReactFlow,
} from "@xyflow/react"

const pathFunctions = {
  default: getBezierPath,
  bezier: getBezierPath,
  smoothstep: getSmoothStepPath,
  step: getSmoothStepPath,
  straight: getStraightPath,
}

const EditableEdge = memo(({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  markerEnd,
  markerStart,
  data,
  selected,
}) => {
  const { setEdges } = useReactFlow()
  const [isEditing, setIsEditing] = useState(false)
  const [labelValue, setLabelValue] = useState(data?.label || "")
  const inputRef = useRef(null)

  const edgeType = data?.edgeType || "default"
  const animated = data?.animated || false
  const getPath = pathFunctions[edgeType] || getBezierPath

  const [edgePath, labelX, labelY] = getPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  })

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  const handleDoubleClick = useCallback((e) => {
    e.stopPropagation()
    setIsEditing(true)
  }, [])

  const handleSave = useCallback(() => {
    setEdges((eds) =>
      eds.map((e) => {
        if (e.id === id) {
          return {
            ...e,
            data: { ...e.data, label: labelValue },
          }
        }
        return e
      })
    )
    setIsEditing(false)
  }, [id, labelValue, setEdges])

  const handleCancel = useCallback(() => {
    setLabelValue(data?.label || "")
    setIsEditing(false)
  }, [data?.label])

  const handleKeyDown = useCallback((e) => {
    if (e.key === "Enter") {
      e.preventDefault()
      handleSave()
    } else if (e.key === "Escape") {
      e.preventDefault()
      handleCancel()
    }
  }, [handleSave, handleCancel])

  const handleBlur = useCallback(() => {
    handleSave()
  }, [handleSave])

  return (
    <>
      <BaseEdge
        id={id}
        path={edgePath}
        markerEnd={markerEnd}
        markerStart={markerStart}
        style={{
          ...style,
          strokeWidth: selected ? 3 : 2,
          stroke: selected ? '#6366f1' : style.stroke || '#64748b',
        }}
        className={animated ? "animated-edge" : ""}
      />
      <EdgeLabelRenderer>
        <div
          style={{
            position: "absolute",
            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
            pointerEvents: "all",
          }}
          className="nodrag nopan"
        >
          {isEditing ? (
            <input
              ref={inputRef}
              type="text"
              value={labelValue}
              onChange={(e) => setLabelValue(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              className="edge-label-input"
              placeholder="Edge label..."
            />
          ) : (
            <div
              onDoubleClick={handleDoubleClick}
              className={`edge-label ${selected ? "selected" : ""} ${data?.label ? "" : "empty"}`}
              title="Double-click to add label"
            >
              {data?.label || (selected ? "Double-click to add label" : "")}
            </div>
          )}
        </div>
      </EdgeLabelRenderer>
    </>
  )
})

EditableEdge.displayName = "EditableEdge"

export default EditableEdge
